import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import RegistrationProgress from './components/RegistrationProgress';
import PersonalProfileStep from './components/PersonalProfileStep';
import AcademicBackgroundStep from './components/AcademicBackgroundStep';
import InterestAssessmentStep from './components/InterestAssessmentStep';
import BenefitsPanel from './components/BenefitsPanel';
import { supabase } from '../../lib/supabase';

const StudentRegistration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    age: '',
    gender: '',
    region: '',
    educationLevel: '',
    series: '',
    performance: '',
    favoriteSubjects: [],
    careerGoal: '',
    studyPreference: '',
    studyDuration: '',
    activities: []
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCheckboxChange = (fieldName, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: checked
        ? [...prev?.[fieldName], value]
        : prev?.[fieldName]?.filter(item => item !== value)
    }));
    if (errors?.[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.firstName?.trim()) newErrors.firstName = 'Le prénom est requis';
      if (!formData?.lastName?.trim()) newErrors.lastName = 'Le nom est requis';
      if (!formData?.email?.trim()) {
        newErrors.email = 'L\'email est requis';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
        newErrors.email = 'Format d\'email invalide';
      }
      if (!formData?.phone?.trim()) {
        newErrors.phone = 'Le numéro de téléphone est requis';
      } else if (!/^\+221\s?\d{2}\s?\d{3}\s?\d{2}\s?\d{2}$/?.test(formData?.phone)) {
        newErrors.phone = 'Format invalide. Utilisez: +221 XX XXX XX XX';
      }
      if (!formData?.password) {
        newErrors.password = 'Le mot de passe est requis';
      } else if (formData?.password?.length < 6) {
        newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
      }
      if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
      }
      if (!formData?.age) newErrors.age = 'L\'âge est requis';
      if (!formData?.gender) newErrors.gender = 'Le genre est requis';
      if (!formData?.region) newErrors.region = 'La région est requise';
    }

    if (step === 2) {
      if (!formData?.educationLevel) newErrors.educationLevel = 'Le niveau d\'études est requis';
      if (!formData?.performance) newErrors.performance = 'La performance académique est requise';
      if (formData?.favoriteSubjects?.length < 3) {
        newErrors.favoriteSubjects = 'Veuillez sélectionner au moins 3 matières';
      }
    }

    if (step === 3) {
      if (!formData?.careerGoal) newErrors.careerGoal = 'L\'objectif de carrière est requis';
      if (!formData?.studyPreference) newErrors.studyPreference = 'Le type d\'études est requis';
      if (!formData?.studyDuration) newErrors.studyDuration = 'La durée d\'études est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setIsSubmitting(true);

    try {
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: formData?.password,
        options: {
          data: {
            full_name: `${formData?.firstName} ${formData?.lastName}`,
            role: 'student'
          }
        }
      });

      if (authError) throw authError;

      const userId = authData?.user?.id;

      // Insert user profile
      if (userId) {
        const { error: profileError } = await supabase
          ?.from('user_profiles')
          ?.upsert({
            id: authData?.user?.id,
            auth_user_id: authData?.user?.id,
            full_name: `${formData?.firstName} ${formData?.lastName}`,
            email: formData?.email,
            phone: formData?.phone,
            age: parseInt(formData?.age),
            gender: formData?.gender,
            region: formData?.region,
            education_level: formData?.educationLevel,
            role: 'student'
          }, { onConflict: 'id' });

        if (profileError) throw profileError;
      }

      // Generate recommendations after successful registration
      if (userId) {
        try {
          // Fetch all fields
          const { data: fields, error: fieldsError } = await supabase
            ?.from('fields')
            ?.select('id, interest');

          if (!fieldsError && fields?.length > 0) {
            const favoriteSubjects = formData?.favoriteSubjects || [];

            // Calculate scores for each field
            const scoredFields = fields?.map(field => {
              let score = 50; // Base score

              // Apply scoring rules based on favorite subjects
              if (field?.interest === 'sciences') {
                if (favoriteSubjects?.includes('mathematiques') ||
                    favoriteSubjects?.includes('physique-chimie') ||
                    favoriteSubjects?.includes('informatique')) {
                  score += 20;
                }
              }

              if (field?.interest === 'health') {
                if (favoriteSubjects?.includes('svt') ||
                    favoriteSubjects?.includes('physique-chimie')) {
                  score += 20;
                }
              }

              if (field?.interest === 'business') {
                if (favoriteSubjects?.includes('economie')) {
                  score += 20;
                }
              }

              if (field?.interest === 'humanities') {
                if (favoriteSubjects?.includes('francais') ||
                    favoriteSubjects?.includes('histoire-geo') ||
                    favoriteSubjects?.includes('philosophie')) {
                  score += 20;
                }
              }

              if (field?.interest === 'arts') {
                if (favoriteSubjects?.includes('arts')) {
                  score += 20;
                }
              }

              return { field_id: field?.id, score };
            });

            // Sort by score desc and take top 3
            const topRecommendations = scoredFields
              ?.sort((a, b) => b?.score - a?.score)
              ?.slice(0, 3)
              ?.map(rec => ({
                user_id: userId,
                field_id: rec?.field_id,
                score: rec?.score
              }));

            // Insert recommendations
            if (topRecommendations?.length > 0) {
              await supabase
                ?.from('user_recommendations')
                ?.insert(topRecommendations);
            }
          }
        } catch (recError) {
          console.error('Recommendation generation error:', recError);
          // Don't block registration on recommendation failure
        }
      }

      console.log('Student registration successful:', authData);
      navigate('/student-dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ submit: error?.message || 'Erreur lors de l\'inscription' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalProfileStep
            formData={formData}
            errors={errors}
            onChange={handleInputChange}
          />
        );
      case 2:
        return (
          <AcademicBackgroundStep
            formData={formData}
            errors={errors}
            onChange={handleInputChange}
            onCheckboxChange={handleCheckboxChange}
          />
        );
      case 3:
        return (
          <InterestAssessmentStep
            formData={formData}
            errors={errors}
            onChange={handleInputChange}
            onCheckboxChange={handleCheckboxChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="content-area">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-6 md:mb-8 lg:mb-10">
            <button
              onClick={() => navigate('/registration-role-selection')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 mb-4 md:mb-6"
            >
              <Icon name="ArrowLeft" size={20} />
              <span className="text-sm md:text-base">Retour</span>
            </button>

            <div className="text-center mb-6 md:mb-8 lg:mb-10">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 md:mb-3">
                Créez Votre Compte Étudiant
              </h1>
              <p className="text-sm md:text-base lg:text-lg text-gray-600">
                Complétez votre profil en 3 étapes simples pour recevoir des recommandations personnalisées
              </p>
            </div>

            <RegistrationProgress currentStep={currentStep} totalSteps={3} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 lg:p-10">
                {renderStepContent()}

                {errors?.submit && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{errors?.submit}</p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mt-6 md:mt-8 lg:mt-10 pt-6 md:pt-8 border-t border-gray-200">
                  {currentStep > 1 && (
                    <Button
                      variant="outline"
                      onClick={handlePrevious}
                      iconName="ChevronLeft"
                      iconPosition="left"
                      className="w-full sm:w-auto"
                    >
                      Précédent
                    </Button>
                  )}

                  {currentStep < 3 ? (
                    <Button
                      variant="default"
                      onClick={handleNext}
                      iconName="ChevronRight"
                      iconPosition="right"
                      className="w-full sm:flex-1"
                    >
                      Continuer
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      onClick={handleSubmit}
                      loading={isSubmitting}
                      iconName="Check"
                      iconPosition="right"
                      className="w-full sm:flex-1"
                    >
                      {isSubmitting ? 'Création en cours...' : 'Créer Mon Compte'}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <BenefitsPanel />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentRegistration;